/**
 * Extension points of the chat screen (frozen signatures, orchestrator). Other features register renderers for the
 * message part types and chat accessories they own (agents: run status and Stop; spaces: project cards; discover:
 * shared posts) at module load of their index.ts, so the chat feature never imports them.
 */
import type { ComponentType } from 'react';
import type { Id, Message, Part } from '@/model/wire';
import type { DisplayMessage, HyloServices } from '@/model';
import type { Navigation } from '@/app/contract';

export interface PartRendererProps {
  part: Part;
  partIndex: number;
  message: Message;
  /** The message is mine (alignment and colors). */
  outgoing: boolean;
}

const partRenderers = new Map<string, ComponentType<PartRendererProps>>();

/** Registers the renderer of one part `type` (one owner per type; a second registration replaces the first). */
export function registerPartRenderer(type: string, renderer: ComponentType<PartRendererProps>): void {
  partRenderers.set(type, renderer);
}

export function partRenderer(type: string): ComponentType<PartRendererProps> | undefined {
  return partRenderers.get(type);
}

export type ChatAccessorySlot = 'header' | 'banner' | 'aboveComposer';

export interface ChatAccessoryProps {
  conversationId: Id;
}

const accessories: Array<{ slot: ChatAccessorySlot; component: ComponentType<ChatAccessoryProps> }> = [];

/** Adds a component to a slot of every chat screen (it renders nothing when it doesn't apply). */
export function registerChatAccessory(slot: ChatAccessorySlot, component: ComponentType<ChatAccessoryProps>): void {
  accessories.push({ slot, component });
}

export function chatAccessories(slot: ChatAccessorySlot): ComponentType<ChatAccessoryProps>[] {
  return accessories.filter((a) => a.slot === slot).map((a) => a.component);
}

// Additive W0-contracts extension points. Existing registries/signatures above remain unchanged.

/** A system-event key is registry metadata, never a new wire part type. Pass the original Part unchanged. */
export function rendererForPart(part: Part): ComponentType<PartRendererProps> | undefined {
  const event = part.type === 'system' && typeof part.event === 'string' ? part.event : undefined;
  return (event ? partRenderer(`system:${event}`) : undefined) ?? partRenderer(part.type);
}

export interface MessageAccessoryProps {
  message: Message;
  outgoing: boolean;
}
const messageAccessories: ComponentType<MessageAccessoryProps>[] = [];
/** Mount only for readable, sent, nontombstoned messages, after their ordinary parts. */
export function registerMessageAccessory(component: ComponentType<MessageAccessoryProps>): void {
  if (!messageAccessories.includes(component)) messageAccessories.push(component);
}
export function messageAccessoriesForChat(): readonly ComponentType<MessageAccessoryProps>[] {
  return [...messageAccessories];
}

export interface MessageGroupRendererProps {
  conversationId: Id;
  items: readonly DisplayMessage[];
  expanded: boolean;
  onToggle(): void;
}
export interface MessageGroupRegistration {
  id: string;
  /** Pure metadata predicate; the host first removes hidden/deleted/local/system rows. */
  matches(message: Message): boolean;
  /** The host also requires consecutive seqs and no unread/day/gap boundary. */
  joins(previous: Message, next: Message): boolean;
  component: ComponentType<MessageGroupRendererProps>;
}
const messageGroups = new Map<string, MessageGroupRegistration>();
/** Duplicate IDs replace in place. The host retains each child's identity, focus, and read evidence. */
export function registerMessageGroupRenderer(registration: MessageGroupRegistration): void {
  messageGroups.set(registration.id, registration);
}
export function messageGroupRenderers(): readonly MessageGroupRegistration[] {
  return [...messageGroups.values()];
}

export interface MessageActionContext {
  conversationId: Id;
  message: Message;
  outgoing: boolean;
  services: HyloServices;
  navigation: Navigation;
}
export interface MessageActionRegistration {
  id: string;
  titleKey: string;
  icon?: ComponentType<{ size?: number }>;
  /** Recheck at execution; the owner must still reauthorize its actual operation. */
  isAvailable(context: MessageActionContext): boolean;
  run(context: MessageActionContext): void | Promise<void>;
}
const actions = new Map<string, MessageActionRegistration>();
export function registerMessageAction(registration: MessageActionRegistration): void {
  actions.set(registration.id, registration);
}
export function messageActions(): readonly MessageActionRegistration[] {
  return [...actions.values()];
}
